import { Grid, Tooltip } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { observer } from 'mobx-react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import ScimManagementModal, { ScimStatusResponse } from '../../../../components/modals/company-sso-modal/scim/ScimManagementModal'
import SelfServiceSSOModal from '../../../../components/modals/company-sso-modal/SelfServiceSSOModal'

import { Elements, Loader } from '@tfo/common'
import { ICompany } from '@tfo/common/src/models'
import { useEffect } from 'react'
import { useStore } from '../../../../hooks/useStore'
import $http from '../../../../utils/$http'
import CustomerPortalTable, { Column } from '../../common/CustomerPortalTable'

const useStyles = makeStyles({
	userList: {
		padding: '1em'
	},
	table: {
		marginTop: '3%',
		minWidth: 650,
	},
	row: {
		'&:hover': {
			cursor: 'pointer',
			background: '#e6e6e6',
		},
	},
	addUser: {
		marginTop: '4%',
		justifyContent: 'center',
		display: 'inline',
		color: '#059ac4',
		'&:hover': {
			cursor: 'pointer',
		},
	},
	resetFilter: {
		alignSelf: 'center',
		padding: '0.5rem',
		color: '#059ac4',
		marginLeft: '1rem',
		'&:hover': {
			cursor: 'pointer',
		},
	},
	scroll: {
		overflowX: 'scroll',
		scrollbarWidth: 'none', //Firefox
		overflow: '-moz-scrollbars-none',
		'&::-webkit-scrollbar': {
			display: 'none' //Chrome & Safari
		}
	},
	itemRow: {
		display: 'flex'
	},
	tfoPinkChip: {
		backgroundColor: '#ec9dc9',
		color: '#f1f1f1'
	},
	tfoPinkColor: {
		color: '#ec9dc9'
	}
})

type CompanyActivation = {
	company: ICompany
	activation: SsoActivationResponse
}

type CompanyActivationRow = {
	companyId: number
	companyName: string
	organizationNumber: string
	displayedActivationState: JSX.Element
	provisioningElement: JSX.Element | null
	directLinkElement: JSX.Element | null
	underlyingItem: CompanyActivation
}

enum SsoConfigurationState {
	Inactive = 'Inactive',
	Pending = 'Pending',
	Active = 'Active'
}

type SsoActivationResponse = {
	companyId: number
	configurationState: SsoConfigurationState
	ssoSystem: string
}

export const ActivateSSO = observer(() => {
	const classes = useStyles()
	const { clientViewStore } = useStore()

	const [showStartSelfServiceModal, setShowStartSelfServiceModal] = React.useState(false)
	const [selectedCompanyActivation, setSelectedCompanyActivation] = React.useState<CompanyActivation | null>(null)
	const [isLoading, setIsLoading] = React.useState(true)
	const [companyActivations, setCompanyActivations] = React.useState<CompanyActivation[]>([])
	const [scimStatuses, setScimStatuses] = React.useState<Record<number, ScimStatusResponse>>({})
	const [showScimModal, setShowScimModal] = React.useState(false)
	const [selectedScimCompany, setSelectedScimCompany] = React.useState<CompanyActivation | null>(null)

	const fetchScimStatuses = (companyIds: number[]) => {
		Promise.allSettled(companyIds.map((id) =>
			$http.get<ScimStatusResponse>(`/authenticate/sso/scim?companyId=${id}`).then((status) => ({ id, status }))
		)).then((results) => {
			setScimStatuses((previous) => {
				const next = { ...previous }
				results.forEach((result) => {
					if (result.status === 'fulfilled') {
						next[result.value.id] = result.value.status
					}
				})
				return next
			})
		})
	}

	useEffect(() => {
		const companyIds = clientViewStore.flatSubsidiaries.map((c) => c.id)
		if (companyIds.length < 1) {
			return
		}

		$http.post<{activations: SsoActivationResponse[]}>('/authenticate/sso/activations', {
			companyIds: companyIds
		})
			.then((response) => {
				setCompanyActivations(() => response.activations.map((a) => {
					return {
						company: clientViewStore.flatSubsidiaries.find((c) => c.id === a.companyId),
						activation: a
					} as CompanyActivation
				}))

				const scimEligibleIds = response.activations
					.filter((a) => a.configurationState === SsoConfigurationState.Active)
					.map((a) => a.companyId)
					.filter((id) => {
						const company = clientViewStore.flatSubsidiaries.find((c) => c.id === id)
						return company && isSSOApplicable(company)
					})
				fetchScimStatuses(scimEligibleIds)
			})
			.finally(() => {
				setIsLoading(false)
			})

	}, [])

	const getColumns: Column[] = [
		{ label: 'company', prop: 'companyName' },
		{ label: 'organizationNumber', prop: 'organizationNumber' },
		{ label: 'sso', prop: 'displayedActivationState' },
		{ label: 'sso.provisioning.scim', prop: 'provisioningElement' },
		{ label: 'sso.directlink', prop: 'directLinkElement' },
	]

	const isSSOApplicable = (company: ICompany): boolean => {
		return company.isSSOEnabled  //need to enabled SSO by backoffice
		&& !company.parentCompanyId  //need to be a root company
	}

	const canConfigureNewSSO = (ca: CompanyActivation): boolean => {
		return isSSOApplicable(ca.company) 
		&& ca.activation.configurationState === SsoConfigurationState.Inactive
	}

	const canReconfigureSSO = (ca: CompanyActivation): boolean => {
		return isSSOApplicable(ca.company) 
			&& (ca.activation.configurationState === SsoConfigurationState.Active 
			|| ca.activation.configurationState === SsoConfigurationState.Pending)
	}

	const activationElementSelector = (a: CompanyActivation) => {
		if (canReconfigureSSO(a)) {
			return  <ReconfigureSSOButton configurationState={a.activation.configurationState}/>
		} else if (canConfigureNewSSO(a)) {
			return <ActivateSSOButton />
		} else {
			return <ActivationNotApplicableSSOLabel />
		}
	}

	const getRows = companyActivations.map((a) => ({
		companyId: a.company.id,
		companyName: a.company.name,
		organizationNumber: a.company.organizationNumber,
		displayedActivationState: activationElementSelector(a),
		provisioningElement: a.activation.configurationState === SsoConfigurationState.Active && isSSOApplicable(a.company)
			? <ProvisioningCell
				scimActive={scimStatuses[a.company.id]?.status === 'Active'}
				onOpen={() => {
					setSelectedScimCompany(a)
					setShowScimModal(true)
				}} />
			: null,
		directLinkElement: a.activation.configurationState === SsoConfigurationState.Active
			? <SsoDirectLink companyId={a.company.id} />
			: null,
		underlyingItem: a,
	} as CompanyActivationRow))

	const onRowClick = (row: CompanyActivationRow) => {
		const item = row.underlyingItem
		
		if(isSSOApplicable(item.company)) {
			setSelectedCompanyActivation(item)
			setShowStartSelfServiceModal(true)
		}
	}

	const canClickRow = (row: CompanyActivationRow): boolean => {
		const item = row.underlyingItem
		return isSSOApplicable(item.company)
	}

	return (
		<>
			{isLoading && <Loader />}
			<Grid container direction="column" className={classes.userList}>
				<Grid item xs={12}>
					<CustomerPortalTable columns={getColumns} rows={getRows} onRowClick={onRowClick} canClickRow={canClickRow} />
				</Grid>
				{selectedCompanyActivation && <SelfServiceSSOModal
					company={selectedCompanyActivation.company}
					open={showStartSelfServiceModal}
					onClose={() => setShowStartSelfServiceModal(false)}
					isReconfigure={canReconfigureSSO(selectedCompanyActivation)} />}
				{selectedScimCompany && <ScimManagementModal
					company={selectedScimCompany.company}
					open={showScimModal}
					onClose={() => setShowScimModal(false)}
					onScimStateChanged={() => fetchScimStatuses([selectedScimCompany.company.id])} />}
			</Grid>

		</>

	)
})

const useButtonStyles = makeStyles({
	button: {
		backgroundColor: '#0098C3',
		color: '#F3F3F1',
		padding: '0.5rem 2rem',
		marginTop: '-0.5rem'
	}
})

const ActivateSSOButton = () => {
	const intl = useIntl()
	const styles = useButtonStyles()
	return (
		<Elements.Button className={styles.button}>
			{intl.formatMessage({id: 'sso.activation.enable'})}
		</Elements.Button>)
}


const reconfigureStyles = makeStyles({
	button: {
		backgroundColor: '#009B48',
		color: '#F3F3F1',
		padding: '0.5rem 2rem',
		marginTop: '-0.5rem'
	}
})

const ReconfigureSSOButton = ({configurationState}: {configurationState: SsoConfigurationState}) => {
	const intl = useIntl()
	const styles = reconfigureStyles()
	return (
		<Elements.Button className={styles.button}>
			{intl.formatMessage({id: `sso.activation.${configurationState.toLowerCase()}`})}
		</Elements.Button>)
}


const notApplicableStyles = makeStyles({
	activated: {
		color: '#666666',
		fontWeight: 'bold',
	},

})

const ActivationNotApplicableSSOLabel = () => {
	const intl = useIntl()
	const styles = notApplicableStyles()
	return (
	<Tooltip arrow placement='top' title={intl.formatMessage({ id: 'sso.activation.na.tooltip' })}>
		<span className={styles.activated}>{intl.formatMessage({ id: 'sso.activation.na' })}</span>
	</Tooltip>)
}

const ProvisioningCell = ({ scimActive, onOpen }: { scimActive: boolean, onOpen: () => void }) => {
	const intl = useIntl()

	const handleClick = (e: React.MouseEvent) => {
		e.stopPropagation()
		onOpen()
	}

	return (
		<Tooltip arrow placement='top' title={intl.formatMessage({ id: 'sso.scim.cell.tooltip' })}>
			{scimActive
				? <Elements.Button className={reconfigureStyles().button} onClick={handleClick}>
					{intl.formatMessage({ id: 'sso.activation.active' })}
				</Elements.Button>
				: <Elements.Button className={useButtonStyles().button} onClick={handleClick}>
					{intl.formatMessage({ id: 'sso.activation.enable' })}
				</Elements.Button>}
		</Tooltip>
	)
}

const SsoDirectLink = ({ companyId }: { companyId: number }) => {
	const intl = useIntl()
	const [copied, setCopied] = React.useState(false)
	const href = `${window.location.origin}/sso/${companyId}`

	const handleCopy = (e: React.MouseEvent) => {
		e.stopPropagation()
		navigator.clipboard.writeText(href).then(() => {
			setCopied(true)
			setTimeout(() => setCopied(false), 2000)
		})
	}

	return (
		<Tooltip arrow placement='top' title={copied ? intl.formatMessage({ id: 'sso.directlink.copied' }) : href}>
			<a  onClick={handleCopy} >
				{href}
			</a>
		</Tooltip>
	)
}

export default ActivateSSO
